import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getGroup, updateGroup, getGroupMembers, deleteGroup } from '../lib/firestore/groups';
import { isAdmin } from '../lib/firestore/admins';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../styles/GroupSettings.css';

const GroupSettings = () => {
  const { groupId } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [groupImageUrl, setGroupImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    visibility: 'invite_only',
    allowSubgroups: true,
    membersCanInvite: true,
    maxSponsorsPerMember: '',
    sponsorEligibilityMinAttended: '',
    sponsorAccountabilityEnabled: false,
    sponsorAccountabilityText: '',
    groupChat: false,
  });
  const [showAccountabilityModal, setShowAccountabilityModal] = useState(false);

  useEffect(() => {
    console.log('🔵 [GroupSettings] useEffect triggered:', { groupId, hasUser: !!user });
    if (!groupId) {
      console.log('🔴 [GroupSettings] Missing groupId');
      navigate('/home');
      return;
    }
    
    if (user) {
      console.log('🔵 [GroupSettings] Starting loadGroupData...');
      loadGroupData();
    } else {
      console.log('🔵 [GroupSettings] No user yet, waiting for auth...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, user]);

  const loadGroupData = async () => {
    if (!groupId || !user) return;

    setLoading(true);
    try {
      const groupData = await getGroup(groupId);
      if (!groupData) {
        setError('Group not found');
        setLoading(false);
        return;
      }

      setGroup(groupData);

      // Check user role (from members list, or from group.ownerId for auto-join/legacy groups)
      const membersList = await getGroupMembers(groupId);
      setMembers(membersList);
      const userMember = membersList.find((m) => m.userId === user.uid);
      const roleFromMembers = userMember?.role || null;
      const isOwnerFromDoc = groupData.ownerId === user.uid;
      setUserRole(roleFromMembers || (isOwnerFromDoc ? 'owner' : null));

      // Only owner/cohost or app admin can edit (owner from members list or from group.ownerId)
      const userIsAdmin = await isAdmin(user.uid);
      const canEditGroup = roleFromMembers === 'owner' || roleFromMembers === 'cohost' || isOwnerFromDoc || userIsAdmin;
      if (!canEditGroup) {
        navigate(`/groups/${groupId}`);
        return;
      }

      // Populate form
      const defaultAccountabilityText = 'You are accountable for any member you invite. If a member breaks the rules you will be suspended from the group';
      setFormData({
        name: groupData.name,
        description: groupData.description || '',
        website: groupData.website || '',
        visibility: groupData.visibility,
        allowSubgroups: groupData.allowSubgroups,
        membersCanInvite: groupData.rules.membersCanInvite,
        maxSponsorsPerMember: groupData.rules.maxSponsorsPerMember?.toString() || '',
        sponsorEligibilityMinAttended:
          groupData.rules.sponsorEligibility?.minAttended?.toString() || '',
        sponsorAccountabilityEnabled: groupData.rules.sponsorAccountabilityEnabled,
        sponsorAccountabilityText: groupData.rules.sponsorAccountabilityText || defaultAccountabilityText,
        groupChat: groupData.groupChat?.enabled || false,
      });
      setGroupImageUrl(groupData.image || null);
    } catch (err) {
      setError(err.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!group || !user) return;

    setSaving(true);
    setError('');

    try {
      const updates = {
        name: formData.name,
        description: formData.description || undefined,
        visibility: formData.visibility,
      };
      if (formData.website && formData.website.trim()) {
        let url = formData.website.trim();
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        updates.website = url;
      } else {
        updates.website = null;
      }
      Object.assign(updates, {
        allowSubgroups: formData.allowSubgroups,
        rules: {
          membersCanInvite: formData.membersCanInvite,
          maxSponsorsPerMember: formData.maxSponsorsPerMember
            ? parseInt(formData.maxSponsorsPerMember)
            : undefined,
          sponsorEligibility: formData.sponsorEligibilityMinAttended
            ? { minAttended: parseInt(formData.sponsorEligibilityMinAttended) }
            : undefined,
          sponsorAccountabilityEnabled: formData.sponsorAccountabilityEnabled,
          sponsorAccountabilityText: formData.sponsorAccountabilityEnabled && formData.sponsorAccountabilityText
            ? formData.sponsorAccountabilityText
            : undefined,
        },
        groupChat: {
          enabled: formData.groupChat,
        },
      });

      // Include image if it was updated
      if (groupImageUrl && groupImageUrl.trim()) {
        updates.image = groupImageUrl.trim();
      } else if (groupImageUrl === null && group.image) {
        // If image was removed (set to null), include null to clear it
        updates.image = null;
      }

      await updateGroup(groupId, updates);

      navigate(`/groups/${groupId}`);
    } catch (err) {
      setError(err.message || 'Failed to update group');
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!user) {
      alert('Please log in to upload images');
      return;
    }

    setUploadingImage(true);
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        setUploadingImage(false);
        return;
      }

      // Upload to Firebase Storage
      const fileRef = ref(storage, `group-images/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setGroupImageUrl(downloadURL);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    if (!confirm('Are you absolutely sure? This will delete the group, all events, and all data permanently.')) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      await deleteGroup(groupId);
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Failed to delete group');
      setSaving(false);
    }
  };

  const isOwner = userRole === 'owner';

  if (loading) {
    return (
      <div className="group-settings-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="group-settings-container">
        <div className="error-message">{error || 'Group not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="group-settings-container">
      <div className="group-settings-card">
        <header className="settings-header">
          <button className="btn-back" onClick={() => navigate(`/groups/${groupId}`)}>
            ← Back
          </button>
          <h1>Group Settings</h1>
          <p>{group.name}</p>
        </header>

        <form onSubmit={handleSubmit} className="settings-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="groupImage">Group Photo</label>
              <div className="image-upload-section">
                <label htmlFor="groupImage" className="image-upload-label">
                  {groupImageUrl ? (
                    <img src={groupImageUrl} alt="Group" className="preview-image" />
                  ) : (
                    <div className="image-upload-placeholder">
                      {uploadingImage ? '⋯' : '📷'}
                    </div>
                  )}
                  <input
                    type="file"
                    id="groupImage"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                  />
                </label>
                {uploadingImage && <div className="uploading-indicator">Uploading...</div>}
                {groupImageUrl && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setGroupImageUrl(null)}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="name">Group Name *</label>
              <input
                type="text"
                id="name"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="input"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
              />
            </div>

            <div className="form-group">
              <label htmlFor="website">Website (Optional)</label>
              <input
                type="url"
                id="website"
                className="input"
                placeholder="https://example.com"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
              <small className="form-hint">
                Link to group website or more info (https:// will be added automatically if missing)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="visibility">Visibility *</label>
              <select
                id="visibility"
                className="input"
                value={formData.visibility}
                onChange={(e) =>
                  setFormData({ ...formData, visibility: e.target.value })
                }
                required
              >
                <option value="invite_only">Invite Only (Private)</option>
                <option value="invite_or_apply">Invite or Apply to Join</option>
                <option value="public">Public (Listed)</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h2>Group Rules</h2>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.allowSubgroups}
                  onChange={(e) =>
                    setFormData({ ...formData, allowSubgroups: e.target.checked })
                  }
                />
                Allow Subgroups
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.membersCanInvite}
                  onChange={(e) =>
                    setFormData({ ...formData, membersCanInvite: e.target.checked })
                  }
                />
                Members Can Invite Others
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.groupChat}
                  onChange={(e) =>
                    setFormData({ ...formData, groupChat: e.target.checked })
                  }
                />
                Allow Group Chat
              </label>
              <small className="form-hint">
                Enable a persistent group chat for all members to communicate anytime
              </small>
            </div>
          </div>

          <div className="form-section">
            <h2>Sponsorship Rules</h2>

            <div className="form-group">
              <label htmlFor="maxSponsors">Max Sponsors Per Member</label>
              <input
                type="number"
                id="maxSponsors"
                className="input"
                min="0"
                placeholder="Leave blank for unlimited"
                value={formData.maxSponsorsPerMember}
                onChange={(e) =>
                  setFormData({ ...formData, maxSponsorsPerMember: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="minAttended">
                Sponsor Eligibility (Min Events Attended)
              </label>
              <input
                type="number"
                id="minAttended"
                className="input"
                min="0"
                placeholder="e.g., 1"
                value={formData.sponsorEligibilityMinAttended}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sponsorEligibilityMinAttended: e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.sponsorAccountabilityEnabled}
                  onChange={(e) => {
                    const defaultAccountabilityText = 'You are accountable for any member you invite. If a member breaks the rules you will be suspended from the group';
                    if (e.target.checked) {
                      // If text is empty, populate with default
                      if (!formData.sponsorAccountabilityText || formData.sponsorAccountabilityText.trim() === '') {
                        setFormData({
                          ...formData,
                          sponsorAccountabilityText: defaultAccountabilityText,
                        });
                      }
                      setShowAccountabilityModal(true);
                    } else {
                      setFormData({
                        ...formData,
                        sponsorAccountabilityEnabled: false,
                        sponsorAccountabilityText: defaultAccountabilityText,
                      });
                    }
                  }}
                />
                Enable Sponsor Accountability
              </label>
              {formData.sponsorAccountabilityEnabled && formData.sponsorAccountabilityText && (
                <small className="form-hint" style={{ marginTop: '0.5rem', display: 'block' }}>
                  {formData.sponsorAccountabilityText}
                </small>
              )}
            </div>
          </div>

          {/* Accountability Rules Modal */}
          {showAccountabilityModal && (
            <div className="modal-overlay" onClick={() => setShowAccountabilityModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Sponsor Accountability Rules</h2>
                <p className="modal-hint">(edit to fit your policies)</p>
                <textarea
                  className="input"
                  rows={6}
                  value={formData.sponsorAccountabilityText}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sponsorAccountabilityText: e.target.value,
                    })
                  }
                  placeholder="Enter accountability rules..."
                  maxLength={500}
                />
                <div className="modal-actions" style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAccountabilityModal(false);
                      setFormData({
                        ...formData,
                        sponsorAccountabilityEnabled: false,
                        sponsorAccountabilityText: '',
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        sponsorAccountabilityEnabled: true,
                      });
                      setShowAccountabilityModal(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/groups/${groupId}`)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {isOwner && (
            <>
              <div className="form-section">
                <h2>Manage Cohosts</h2>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(`/groups/${groupId}/cohosts`)}
                  disabled={saving}
                >
                  Manage Cohosts
                </button>
                <p className="form-hint">
                  Add or remove cohosts who can edit the group, invite/sponsor members, and create subgroups/events.
                </p>
              </div>

              <div className="danger-zone">
                <h3>Danger Zone</h3>
                {!showDeleteConfirm ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={saving}
                    >
                      Delete Group
                    </button>
                    <p className="danger-hint">
                      Permanently delete this group and all its events. This action cannot be undone.
                    </p>
                  </>
                ) : (
                <div className="delete-confirmation">
                  <p className="delete-warning-text">
                    <strong>Warning:</strong> This will permanently delete:
                  </p>
                  <ul className="delete-consequences">
                    <li>This group</li>
                    <li>All events in this group</li>
                    <li>All member data</li>
                    <li>All message boards</li>
                  </ul>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="deleteConfirm">
                      Type <strong>DELETE</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      id="deleteConfirm"
                      className="input"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDeleteGroup}
                      disabled={saving || deleteConfirmText !== 'DELETE'}
                    >
                      {saving ? 'Deleting...' : 'Delete Group'}
                    </button>
                  </div>
                </div>
              )}
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default GroupSettings;

